import { hasValue, log, notEmpty } from "@repo/helpers";
import * as format from "@repo/helpers/src/format";

import { getPropertyNameRecursive, localizeTraversedName, traverseProperty } from "./dynamicProperties";

/** Regex that identifies properties embedded in templates */
const TEMPLATE_PROP_MATCH = /#= ?([:\w. [\]]+) ?#/g;

export function Templator<TProps>(
	obj: TProps,
	rootProperty: string,
	template: string,
	index?: number,
	forDisplay = true,
) {
	/**
	 *
	 * @param obj Parent Object with data to fill template with
	 * @param rootProperty Section name, for localizing, aka filling "this"
	 * @param template Template to replace with data values
	 * @param index If property is iterated (i.e. "display this line for every item in this array"),
	 * index is this index of this particular iteration
	 * @param forDisplay
	 * @returns Filled template
	 */
	function fill() {
		const { values } = breakdownTemplate(obj, rootProperty, template, index, forDisplay);
		return buildTemplateString(template, values);
	}

	function breakdown() {
		if (!template) return { values: [], properties: [] };
		const matches = template.matchAll(TEMPLATE_PROP_MATCH);
		const values = [];
		const properties = [];
		let currentMatch = matches.next();
		while (!currentMatch.done) {
			let matchValue = currentMatch.value[1] as string;
			if (matchValue === "index" && hasValue(index)) {
				properties.push(`${rootProperty}[${index}]`);
				values.push(index + 1);
			} else {
				let formatFn: (value: any) => string;
				if (matchValue.startsWith("format")) {
					const formatName = matchValue.split(":")[0].substring(7);
					formatFn = format[formatName];
					matchValue = matchValue.split(":")[1];
					if (!formatFn) log(`No matching format found for ${formatName} for Prop ${matchValue}`, "error");
				}
				const name = localizeTraversedName(matchValue, rootProperty);
				properties.push(getPropertyNameRecursive(name, obj));
				let value = traverseProperty(name, obj);
				if (forDisplay) {
					if (value === true) {
						value = "Yes";
					}
					if (value === false) {
						value = "No";
					}
				}
				if (formatFn) value = formatFn(value);
				values.push(value);
			}
			currentMatch = matches.next();
		}
		return { values, properties };
	}

	function buildString(values: string | number[]) {
		const stringReplace = new RegExp(TEMPLATE_PROP_MATCH, "");
		let stringValue = "" + template;
		for (const value of values) {
			stringValue = stringValue.replace(stringReplace, () =>
				Array.isArray(value) ? JSON.stringify(value) : String(value ?? ""),
			);
		}
		return stringValue;
	}
	return { fill, breakdown, buildString };
}

export function fillTemplate<TProps>(
	obj: TProps,
	rootProperty: string,
	template: string,
	index?: number,
	forDisplay = true,
) {
	const { values } = breakdownTemplate(obj, rootProperty, template, index, forDisplay);
	return buildTemplateString(template, values);
}

export function breakdownTemplate<TProps>(
	obj: TProps,
	rootProperty: string,
	template: string,
	index?: number,
	forDisplay = true,
) {
	if (!template) return { values: [], properties: [] };
	const matches = template.matchAll(TEMPLATE_PROP_MATCH);
	const values = [];
	const properties = [];
	let currentMatch = matches.next();
	while (!currentMatch.done) {
		let matchValue = currentMatch.value[1] as string;
		if (matchValue === "index" && hasValue(index)) {
			properties.push(`${rootProperty}[${index}]`);
			values.push(index + 1);
		} else {
			let formatFn: (value: any) => string;
			if (matchValue.startsWith("format")) {
				const formatName = matchValue.split(":")[0].substring(7);
				formatFn = format[formatName];
				matchValue = matchValue.split(":")[1];
				if (!formatFn) log(`No matching format found for ${formatName} for Prop ${matchValue}`, "error");
			}
			const name = localizeTraversedName(matchValue, rootProperty);
			properties.push(getPropertyNameRecursive(name, obj));
			let value = traverseProperty(name, obj);
			if (forDisplay) {
				if (value === true) {
					value = "Yes";
				}
				if (value === false) {
					value = "No";
				}
			}
			if (formatFn) value = formatFn(value);
			values.push(value);
		}
		currentMatch = matches.next();
	}
	return { values, properties };
}

export function buildTemplateString(template: string, values: string | number[]) {
	const stringReplace = new RegExp(TEMPLATE_PROP_MATCH, "");
	let stringValue = "" + template;
	for (const value of values) {
		stringValue = stringValue.replace(stringReplace, () => String(value));
	}
	return stringValue;
}
